import React from "react";
import { Badge } from "@/components/ui/badge";
import {
  Bell,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Users,
  FileText,
  GraduationCap,
  ChevronRight
} from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";

const notificationIcons = {
  event_invitation: Mail,
  event_reminder: Clock,
  event_updated: AlertCircle,
  invitation_confirmed: CheckCircle,
  invitation_declined: XCircle,
  application_accepted: CheckCircle,
  application_declined: XCircle,
  staff_confirmed: Users,
  staff_available: Users,
  document_published: FileText,
  training_expiry: GraduationCap
};

const notificationColors = {
  event_invitation: "bg-blue-100 text-blue-800",
  event_reminder: "bg-amber-100 text-amber-800",
  event_updated: "bg-purple-100 text-purple-800",
  invitation_confirmed: "bg-emerald-100 text-emerald-800",
  invitation_declined: "bg-red-100 text-red-800",
  application_accepted: "bg-emerald-100 text-emerald-800",
  application_declined: "bg-red-100 text-red-800",
  staff_confirmed: "bg-emerald-100 text-emerald-800",
  staff_available: "bg-blue-100 text-blue-800",
  document_published: "bg-orange-100 text-orange-800",
  training_expiry: "bg-amber-100 text-amber-800"
};

export function getNotificationUrl(notification) {
  if (notification.action_url) return notification.action_url;
  if (notification.event_id) return `/event/${notification.event_id}`;
  if (notification.type === "training_expiry") return "/training";
  return null;
}

export default function NotificationList({ notifications, isLoading, onNotificationClick }) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[rgb(var(--brand-yellow))]" />
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="text-center py-12">
        <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No notifications yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {notifications.map((notification) => {
        const Icon = notificationIcons[notification.type] || Bell;
        const isUnread = !notification.is_read;
        const hasLink = !!getNotificationUrl(notification);

        return (
          <div
            key={notification.id}
            onClick={() => onNotificationClick(notification)}
            className={`w-full text-left p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
              isUnread
                ? "bg-blue-50 border-blue-100 hover:bg-blue-100"
                : "bg-white border-gray-100 hover:bg-gray-50"
            }`}
          >
            <div className="flex gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${notificationColors[notification.type] || "bg-gray-100 text-gray-800"}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className={`font-semibold text-sm ${
                    isUnread ? "text-gray-900" : "text-gray-700"
                  }`}>
                    {notification.title}
                  </h4>
                  {isUnread && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1" />
                  )}
                </div>
                <p className={`text-sm mb-2 ${
                  isUnread ? "text-gray-700" : "text-gray-600"
                }`}>
                  {notification.message}
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {formatDistanceToNow(parseISO(notification.created_date), { addSuffix: true })}
                  </p>
                  {hasLink && (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
